import is from '@sindresorhus/is';
import { z } from 'zod';

import { claimNetworkErrorReport } from '../http-fetcher/network-error-report';
import type { ValidatingHttpFetcherWithBaseUrl } from '../validating-http-fetcher';
import type { FeatureFlagService, FeatureName, FeatureSet } from './feature-flag-service';
import { featureSetSchema } from './feature-flag-service';

export type PrivacyProxyFeatureFlagServiceDependencies = {
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

const flagsPathname = '/privacy-proxy/flags';

const usagePingIntervalInMilliseconds = 5 * 60 * 1000;

// The backend resolves every client-side flag server-side (driven by the JWT) and returns
// LaunchDarkly's per-flag envelope. We only consume `value`; the remaining fields are ignored.
const resolvedFlagsResponseSchema = z.record(z.string(), z.object({ value: z.unknown() }));

const toFeatureSet = (response: z.infer<typeof resolvedFlagsResponseSchema>): FeatureSet =>
    featureSetSchema.parse(Object.fromEntries(Object.entries(response).map(([key, flag]) => [key, flag.value])));

export const createPrivacyProxyFeatureFlagService = (
    dependencies: PrivacyProxyFeatureFlagServiceDependencies,
): FeatureFlagService => {
    const { validatingRealtimeApiFetcher } = dependencies;

    // Every flag stays off until the snapshot resolves, mirroring LaunchDarkly's default-variation fallback.
    let featureSet: FeatureSet = featureSetSchema.parse({});

    const lastUsagePingAt = new Map<FeatureName, number>();

    const pingFeatureFlagUsage = (featureFlagName: FeatureName): void => {
        const now = Date.now();
        const lastPingAt = lastUsagePingAt.get(featureFlagName);

        if (!is.nullOrUndefined(lastPingAt) && now - lastPingAt < usagePingIntervalInMilliseconds) {
            return;
        }

        lastUsagePingAt.set(featureFlagName, now);

        // A usage ping is fire-and-forget telemetry on an already-resolved flag, so its failure
        // is claimed to keep it out of the logs. Failing to load the flag values is the
        // opposite: `initialize` throws and the query that runs it owns that report.
        void validatingRealtimeApiFetcher
            .get(z.boolean(), { pathname: `${flagsPathname}/${featureFlagName}` })
            .then((result) => {
                if (result.isErr) {
                    claimNetworkErrorReport(result.error);
                }
            })
            .catch(() => undefined);
    };

    return {
        async initialize() {
            const result = await validatingRealtimeApiFetcher.get(resolvedFlagsResponseSchema, {
                pathname: flagsPathname,
            });

            if (result.isOk) {
                featureSet = toFeatureSet(result.value);

                return featureSet;
            }

            throw result.error;
        },
        isFeatureFlagEnabled(featureFlagName) {
            pingFeatureFlagUsage(featureFlagName);

            return featureSet[featureFlagName];
        },
    };
};
